import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/authContext";
import createAxiosInstance from "../../../utils/axiosConfig";
import {
	get_questions_by_flow_id,
	question_flows,
	framework_domains,
	get_framework_options,
} from "../../../utils/endpoints";
import { Question, QuestionFlow } from "../../../utils/interfaces";
import Alert from "../../common/alerts/alert";
import Questions from "../../common/maturityAssessment/questions";

const MaturityQuestionnaireSideScreen = ({
	domainID,
	questionFlowID,
	isOpen,
	setIsOpen,
	frameworkId
}: {
	domainID?: number,
	questionFlowID?: number,
	isOpen: boolean,
	setIsOpen: (value: boolean) => void,
	frameworkId: number
}) => {
	const navigate = useNavigate();
	const { logout } = useAuth();
	const [selectedFlow, setSelectedFlow] = useState<QuestionFlow>();
	const [questions, setQuestions] = useState<Question[]>([]);


	const [alertShow, setAlertShow] = useState<boolean>(false);
	const [alertMessage, setAlertMessage] = useState<string>("");
	const [alertType, setAlertType] = useState<
		| "primary"
		| "secondary"
		| "success"
		| "danger"
		| "warning"
		| "info"
		| "light"
		| "dark"
	>("danger");


	const handleQuit = () => {
		// setQuestions([]);
		setAlertShow(false);
		setIsOpen(false);
		// navigate(-1);
	};

	const handleAlert = ({
		message,
		show = true,
		type = "danger",
	}: {
		message: string;
		show: boolean;
		type:
			| "primary"
			| "secondary"
			| "success"
			| "danger"
			| "warning"
			| "info"
			| "light"
			| "dark";
	}) => {
		setAlertMessage(message);
		setAlertShow(show);
		setAlertType(type);
	};

	const fetchQuestionsFlow = async () => {
		if (!domainID) return;

		try {
			// First fetch the domain to get its category_id (framework_id)
			const questionsFlowResponse = await createAxiosInstance().get(
				question_flows(Number(domainID))
			);

			if (
				questionsFlowResponse.status == 401 ||
				questionsFlowResponse.status == 403
			) {
				// 401 is for expired token
				// 403 is for forbidden access (blocked by GPT.)
				console.log("user token expired");
				logout();
				navigate("/");
				return;
			}

			// Loop through the question flows and select the one that matches the questionFlowId
			const flows = questionsFlowResponse.data;
			const matchingFlow = flows.find(
				(flow: QuestionFlow) => flow.id === Number(questionFlowID)
			);
			setSelectedFlow(matchingFlow);

			// If we found a matching flow, fetch scoring options
			if (matchingFlow) {
				try {
					const scoringOptionsResponse = await createAxiosInstance().get(
						get_framework_options(matchingFlow.domain_id)
					);
					if (scoringOptionsResponse.status === 200) {
						console.log('🎯 Scoring Options for Framework:', scoringOptionsResponse.data);
					}
				} catch (error) {
					console.error('Error fetching scoring options:', error);
				}
			}
		} catch (e: any) {
			console.log(e);
		}
	};

	useEffect(() => {
		const getQuestions = async () => {
			if (!questionFlowID) return;

			try {
				const response = await createAxiosInstance().get(
					get_questions_by_flow_id(Number(questionFlowID))
				);
				if (response.status == 401 || response.status == 403) {
					// 401 is for expired token
					// 403 is for forbidden access (blocked by GPT.)
					console.log("user token expired");
					logout();
					return;
				}

				setQuestions(response.data);
			} catch (e: any) {
				if (e.response.status == 402) {
					console.log(e.response.data.detail);
					handleQuit();
					setAlertShow(true);
					setAlertMessage(e.response.data.detail);
					setAlertType("danger");
					return;
				}
				console.log(e);
			}
		};

		getQuestions();
		fetchQuestionsFlow();
	}, [questionFlowID]);

    if (!isOpen) return null;
    
	return (
		<>
            {questionFlowID && selectedFlow && (
                <Questions
                    selectedFlow={selectedFlow}
                    questions={questions}
                    handleQuit={handleQuit}
                    frameworkId={frameworkId}
                    handleAlert={(alert: { message: any; show: any; type: any }) =>
                        handleAlert({
                            message: alert.message,
                            show: alert.show,
                            type: alert.type,
                        })
                    }
                />
            )}
			<Alert
				message={alertMessage}
				show={alertShow}
				onClose={() => setAlertShow(false)}
				type={alertType}
			/>
		</>
	);
};

export default MaturityQuestionnaireSideScreen;
